import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '@/backend/features/background-check';
import { BackgroundCheckConfig } from '@/backend/features/background-check/defaults';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus, BackgroundCheckType, backgroundCheckStatuses } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { BackgroundCheckHistoryExpandableRow } from '@/ui/features/background-check/components/background-check-user-account-section/components/BackgroundCheckHistoryExpandableRow';
import { getBackgroundCheckStatusIcon } from '@/ui/features/background-check/components/background-check-user-account-section/utils/getBackgroundCheckStatusIcon';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Row } from '@juvare/platform-shared/features/storage';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

export interface SingleBackgroundCheckBlockComponentRef {
    reload: () => Promise<void>;
}

interface SingleBackgroundCheckBlockProps {
    backgroundCheckConfig: BackgroundCheckConfig;
    singleBackgroundCheckData: Row<BackgroundCheckColumns>;
    accountData: ExtendedAccountData;
    accountId: string;
    allowManageBackgroundCheckData: boolean;
    handleOpenEditStatusModal: (backgroundCheck: BackgroundCheckColumns) => void;
}

const limit = 5;
const isLastPage = (total: number, page: number) => {
    return page * limit >= total;
};

const SingleBackgroundCheckBlock = React.forwardRef<SingleBackgroundCheckBlockComponentRef, SingleBackgroundCheckBlockProps>(
    (
        {
            backgroundCheckConfig,
            singleBackgroundCheckData,
            accountData,
            accountId,
            allowManageBackgroundCheckData,
            handleOpenEditStatusModal,
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const backgroundCheckHistoryClient = useStorageClient<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
            backgroundCheckHistoryEntityName,
        );

        const {
            size,
            setSize,
            isValidating,
            data: backgroundCheckHistoryPages,
            mutate: mutateBackgroundCheckHistoryPages,
        } = useSWRInfinite(
            (pageIndex, previousPageData) => {
                if (previousPageData && isLastPage(previousPageData.total, pageIndex)) {
                    return null;
                }
                return [
                    `BackgroundCheckAccountComponent-${singleBackgroundCheckData.columns.type}`,
                    allowManageBackgroundCheckData,
                    pageIndex,
                ];
            },
            ([, , pageIndex]) => {
                if (!allowManageBackgroundCheckData) {
                    return { rows: [], total: 0 };
                }

                return backgroundCheckHistoryClient.list({
                    limit: limit,
                    offset: pageIndex * limit,
                    orderBy: [{ column: 'createdAt', desc: true }],
                    filter: [
                        {
                            column: 'accountId',
                            op: '=',
                            value: accountId,
                        },
                        {
                            column: 'type',
                            op: '=',
                            value: singleBackgroundCheckData.columns.type as BackgroundCheckType,
                        },
                    ],
                });
            },
            { initialSize: 1, fallbackData: [{ rows: [], total: 0 }] },
        );

        React.useImperativeHandle(ref, () => ({
            reload: async () => {
                await mutateBackgroundCheckHistoryPages();
            },
        }));

        const totalBackgroundCheckHistoryRecords = backgroundCheckHistoryPages?.[0]?.total ?? 0;

        return (
            <Box p={2}>
                <Box sx={{ backgroundColor: themeColors.surface.secondary }}>
                    <Box
                        p={2}
                        pr={0}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        data-test='background-check-section'
                    >
                        <Box>
                            {singleBackgroundCheckData.columns.type && (
                                <Typography variant='subtitle2'>
                                    {t(
                                        `backgroundCheck.labels.${backgroundCheckConfig.type[singleBackgroundCheckData.columns.type as BackgroundCheckType].process}`,
                                    )}
                                </Typography>
                            )}
                            {singleBackgroundCheckData.columns.updatedAt && (
                                <Typography variant='overline'>
                                    {t('date', {
                                        value: singleBackgroundCheckData.columns.updatedAt,
                                    })}
                                </Typography>
                            )}
                        </Box>
                        <Box display='flex' alignItems='center'>
                            {singleBackgroundCheckData.columns.status && (
                                <>
                                    {getBackgroundCheckStatusIcon(singleBackgroundCheckData.columns.status as BackgroundCheckStatus)}
                                    <Typography variant='body2' pl={1}>
                                        {backgroundCheckStatuses[singleBackgroundCheckData.columns.status as BackgroundCheckStatus] ??
                                            backgroundCheckStatuses.unknown}
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <Box mr={2}>
                            {allowManageBackgroundCheckData && (
                                <Button
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() => handleOpenEditStatusModal(singleBackgroundCheckData.columns)}
                                >
                                    {t('backgroundCheck.labels.editStatus')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
                {allowManageBackgroundCheckData && (
                    <BackgroundCheckHistoryExpandableRow
                        size={size}
                        total={totalBackgroundCheckHistoryRecords}
                        setSize={setSize}
                        mutate={mutateBackgroundCheckHistoryPages}
                        isValidating={isValidating}
                        accountData={accountData}
                        backgroundCheck={singleBackgroundCheckData.columns}
                        accountId={accountId}
                        isLastPage={isLastPage(totalBackgroundCheckHistoryRecords, size)}
                        backgroundCheckHistoryPages={backgroundCheckHistoryPages ?? []}
                    />
                )}
            </Box>
        );
    },
);

export default SingleBackgroundCheckBlock;
